import { motion } from 'framer-motion';
import { BookOpen, Calendar, ArrowRight, FileText, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { research } from '../data/research';

const Research = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section
      id="research"
      className="bg-gradient-to-b from-gray-900 to-black px-4 py-20 sm:px-6 lg:px-8"
    >
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 className="mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-4xl font-bold text-transparent sm:text-5xl">
            Publications
          </h2>
          <div className="mx-auto mb-4 h-1 w-24 bg-gradient-to-r from-blue-500 to-red-500"></div>
          <p className="text-lg text-gray-400">Research Publications ({research.length})</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
        >
          {research.map(item => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              whileHover={{ scale: 1.05, y: -10 }}
              className="group cursor-pointer rounded-xl border border-white/10 bg-white/5 p-6 backdrop-blur-md transition-all duration-300 hover:bg-white/10"
              onClick={e => {
                if (!e.target.closest('a')) {
                  navigate(`/research/${item.id}`);
                }
              }}
            >
              <div className="mb-4 flex items-start justify-between">
                <div className="rounded-lg bg-gradient-to-br from-blue-500/20 to-red-500/20 p-3 transition-all group-hover:from-blue-500/30 group-hover:to-red-500/30">
                  {item.type === 'Article' ? (
                    <FileText className="h-6 w-6 text-blue-400" />
                  ) : (
                    <BookOpen className="h-6 w-6 text-blue-400" />
                  )}
                </div>
                <span className="rounded-full border border-blue-500/30 bg-blue-500/20 px-3 py-1 text-xs text-blue-400">
                  {item.type}
                </span>
              </div>
              <h3 className="mb-3 line-clamp-2 text-lg font-bold text-white transition-colors group-hover:text-blue-400">
                {item.title}
              </h3>
              <div className="mb-2 flex items-center gap-2 text-xs text-gray-400">
                <Calendar className="h-3 w-3" />
                <span>{item.date}</span>
              </div>
              <p className="mb-3 text-xs text-gray-500">{item.author}</p>
              <p className="mb-4 line-clamp-3 text-sm text-gray-400">{item.description}</p>
              {item.fullTextAvailable && (
                <span className="mb-3 inline-block text-xs text-green-400">
                  Full-text available
                </span>
              )}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate(`/research/${item.id}`)}
                  className="flex items-center gap-2 text-sm font-semibold text-blue-400 transition-colors hover:text-blue-300"
                >
                  View Details
                  <ArrowRight className="h-4 w-4" />
                </button>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs font-medium text-gray-400 transition-colors hover:text-blue-400"
                  >
                    ResearchGate
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Research;
